import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useCurrentOrgId } from "../auth";
import { useToast } from "../use-toast";
import { useState } from "react";
import { validateDealFile, sanitizeStorageFileName } from "@/lib/fileUtils";

export type DealFile = Database["public"]["Tables"]["deal_files"]["Row"] & {
  /** Vínculo opcional com a atividade (nota) à qual o arquivo pertence. */
  activity_id: string | null;
};

const SIGNED_URL_TTL_SECONDS = 3600;

export function useDealFiles(dealId: string) {
  const { orgId } = useCurrentOrgId();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  const { data: files = [], isLoading } = useQuery({
    queryKey: ["deal-files", dealId, orgId],
    queryFn: async () => {
      if (!orgId || !dealId) return [];

      const { data, error } = await supabase
        .from("deal_files")
        .select("*")
        .eq("deal_id", dealId)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (error) {
        if (error.code === "42P01" || error.message?.includes("does not exist")) {
          return [];
        }
        console.error("Error fetching deal files:", error);
        return [];
      }

      return (data || []) as DealFile[];
    },
    enabled: !!orgId && !!dealId,
  });

  /** URL assinada para leitura (bucket privado). Válida por 1 hora. */
  const getFileUrl = async (filePath: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from("deal-files")
      .createSignedUrl(filePath, SIGNED_URL_TTL_SECONDS);

    if (error) {
      console.error("Error creating signed URL:", error);
      toast({
        title: "Erro ao abrir arquivo",
        description: error.message || "Não foi possível gerar o link do arquivo.",
        variant: "destructive",
      });
      return null;
    }

    return data.signedUrl;
  };

  /** URL assinada a partir do id do arquivo (usado por eventos da timeline). */
  const getFileUrlById = async (fileId: string): Promise<string | null> => {
    const { data, error } = await supabase
      .from("deal_files")
      .select("file_path")
      .eq("id", fileId)
      .maybeSingle();

    if (error || !data?.file_path) return null;
    return getFileUrl(data.file_path);
  };

  const uploadFile = async (file: File, opts?: { activityId?: string | null }) => {
    if (!orgId || !dealId) return false;

    const validationError = validateDealFile(file);
    if (validationError) {
      toast({
        title: "Não foi possível anexar",
        description: validationError,
        variant: "destructive",
      });
      return false;
    }

    setIsUploading(true);
    let uploadedPath: string | null = null;
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("User not authenticated");

      const filePath = `${orgId}/${dealId}/${Date.now()}_${sanitizeStorageFileName(file.name)}`;
      const { error: uploadError } = await supabase.storage
        .from("deal-files")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      uploadedPath = filePath;

      const { error: insertError } = await supabase.from("deal_files").insert({
        deal_id: dealId,
        org_id: orgId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type,
        uploaded_by: user.user.id,
        ...(opts?.activityId ? { activity_id: opts.activityId } : {}),
      });

      if (insertError) throw insertError;

      queryClient.invalidateQueries({ queryKey: ["deal-files", dealId, orgId] });
      queryClient.invalidateQueries({ queryKey: ["deal-events", dealId] });
      toast({
        title: "Arquivo enviado",
        description: `${file.name} foi anexado com sucesso.`,
      });
      return true;
    } catch (error: unknown) {
      console.error("Error uploading file:", error);
      // Clean up orphaned storage file if upload succeeded but DB insert failed
      if (uploadedPath) {
        await supabase.storage.from("deal-files").remove([uploadedPath]).catch(() => {});
      }
      const message = error instanceof Error ? error.message : "Não foi possível enviar o arquivo.";
      toast({
        title: "Erro ao enviar arquivo",
        description: message,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = async (fileId: string, filePath: string) => {
    if (!orgId) return;

    try {
      await supabase.storage.from("deal-files").remove([filePath]);

      const { error } = await supabase
        .from("deal_files")
        .delete()
        .eq("id", fileId)
        .eq("org_id", orgId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["deal-files", dealId, orgId] });
      queryClient.invalidateQueries({ queryKey: ["deal-events", dealId] });
      toast({
        title: "Arquivo removido",
        description: "O arquivo foi removido com sucesso.",
      });
    } catch (error: unknown) {
      console.error("Error removing file:", error);
      const message = error instanceof Error ? error.message : "Não foi possível remover o arquivo.";
      toast({
        title: "Erro ao remover arquivo",
        description: message,
        variant: "destructive",
      });
    }
  };

  return {
    files,
    isLoading,
    isUploading,
    uploadFile,
    removeFile,
    getFileUrl,
    getFileUrlById,
  };
}
